/**
 * Customer Intelligence Panel
 * 
 * Advanced right panel for the professional 3-panel interface featuring:
 * - Comprehensive customer profile with engagement analytics
 * - Device, browser, and location intelligence
 * - Visit history and customer journey tracking
 * - Quick action buttons for agent productivity
 * - Professional design with scalable architecture
 * 
 * This component leverages existing backend data from ConversationSession
 * and provides rich customer insights for agents.
 * 
 * @version 1.0.0
 * @author CleverAct AI Team
 */

import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '../../../ui/Card'
import { Badge } from '../../../ui/Badge'
import { Button } from '../../../ui/Button'
import { 
  User,
  Mail,
  Phone,
  MapPin,
  Globe,
  Clock,
  Smartphone,
  Monitor,
  Tablet,
  Calendar,
  MessageSquare,
  Star,
  TrendingUp,
  TrendingDown,
  Activity,
  Eye,
  ExternalLink,
  UserPlus,
  UserMinus,
  Flag,
  AlertTriangle,
  CheckCircle,
  CheckCircle2,
  Tag,
  StickyNote,
  Send,
  UserCheck,
  RotateCcw,
  MoreVertical,
  Heart,
  ThumbsUp,
  ArrowRight,
  RefreshCw
} from 'lucide-react'
import { logger } from '../../../../utils/logger'
import { api } from '../../../../services/api'
import { conversationHistoryService } from '../../../../services/conversationHistory'
import { liveChatService } from '../../../../services/liveChat'
import { useToast } from '../../../../hooks/useToast'
import { useAuth } from '../../../../hooks/useAuth'
import { PrioritySelector } from '../PrioritySelector'
import type { EnhancedConversationAssignment, CustomerIntelligence } from './ProfessionalConversationView'

/**
 * Customer intelligence panel props
 */
export interface CustomerIntelligencePanelProps {
  /** Current conversation with customer data */
  conversation: EnhancedConversationAssignment | null
  /** Processed customer intelligence data */
  customerIntelligence: CustomerIntelligence | null
  /** Current authenticated user */
  currentUser?: any
  /** Current view mode */
  viewMode?: 'standard' | 'focus' | 'analytics'
  /** Custom CSS classes */
  className?: string
  /** Callback when assignment is completed successfully */
  onAssignmentComplete?: (result?: any) => void
  /** Callback when priority is updated */
  onPriorityUpdate?: (conversationId: string, newPriority: string, oldPriority: string) => void
}

/**
 * Device type configuration with icons and colors
 */
const DEVICE_CONFIG = {
  mobile: {
    icon: Smartphone,
    label: 'Mobile Device',
    color: 'text-blue-600',
    bgColor: 'bg-blue-50',
    borderColor: 'border-blue-200'
  },
  desktop: {
    icon: Monitor,
    label: 'Desktop Computer',
    color: 'text-slate-600',
    bgColor: 'bg-slate-50',
    borderColor: 'border-slate-200'
  },
  tablet: {
    icon: Tablet,
    label: 'Tablet Device',
    color: 'text-purple-600',
    bgColor: 'bg-purple-50',
    borderColor: 'border-purple-200'
  },
  unknown: {
    icon: Globe,
    label: 'Unknown Device',
    color: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200'
  }
} as const

/**
 * Engagement score configuration with visual indicators
 */
const ENGAGEMENT_LEVELS = {
  high: {
    label: 'High Engagement',
    color: 'text-emerald-700',
    bgColor: 'bg-emerald-50',
    borderColor: 'border-emerald-200',
    icon: TrendingUp,
    threshold: 70
  },
  medium: {
    label: 'Medium Engagement',
    color: 'text-amber-700',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
    icon: Activity,
    threshold: 40
  },
  low: {
    label: 'Low Engagement',
    color: 'text-red-700',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
    icon: TrendingDown,
    threshold: 0
  }
} as const

/**
 * Quick action button configuration
 */
const QUICK_ACTIONS = [
  {
    id: 'assign',
    label: 'Assign to Me',
    icon: UserCheck,
    color: 'bg-blue-600 hover:bg-blue-700',
    description: 'Take ownership of this conversation'
  },
  {
    id: 'transfer',
    label: 'Transfer to AI',
    icon: RotateCcw,
    color: 'bg-purple-600 hover:bg-purple-700',
    description: 'Hand back to AI assistant'
  },
  {
    id: 'resolve',
    label: 'Mark Resolved',
    icon: CheckCircle,
    color: 'bg-emerald-600 hover:bg-emerald-700',
    description: 'Close this conversation'
  },
  {
    id: 'escalate',
    label: 'Assign to',
    icon: AlertTriangle,
    color: 'bg-amber-600 hover:bg-amber-700',
    description: 'Assign to supervisor'
  }
] as const

/**
 * Format session duration for display
 */
const formatDuration = (seconds: number): string => {
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`
  return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`
}

/**
 * Get engagement level based on score
 */
const getEngagementLevel = (score: number) => {
  if (score >= ENGAGEMENT_LEVELS.high.threshold) return ENGAGEMENT_LEVELS.high
  if (score >= ENGAGEMENT_LEVELS.medium.threshold) return ENGAGEMENT_LEVELS.medium
  return ENGAGEMENT_LEVELS.low
}

/**
 * Customer Profile Header Component
 * 
 * Displays essential customer information and online status
 */
const CustomerProfileHeader: React.FC<{
  intelligence: CustomerIntelligence
  conversation: EnhancedConversationAssignment
}> = ({ intelligence, conversation }) => {
  
  const deviceConfig = DEVICE_CONFIG[intelligence.device.type] || DEVICE_CONFIG.unknown
  const engagementLevel = getEngagementLevel(intelligence.engagement.engagementScore)
  
  return (
    <div className="space-y-4">
      
      {/* Customer Identity */}
      <div className="flex items-start gap-3">
        <div className="relative">
          <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
            <span className="text-white font-semibold text-lg">
              {intelligence.profile.name.charAt(0).toUpperCase()}
            </span>
          </div>
          {intelligence.profile.isOnline && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full animate-pulse" />
          )}
        </div>
        
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-gray-900 truncate">
            {intelligence.profile.name}
          </h3>
          <div className="text-sm text-gray-600 flex items-center gap-1">
            {intelligence.profile.isOnline ? (
              <>
                <span className="text-emerald-600">Online now</span>
                <div className="w-1.5 h-1.5 bg-emerald-500 rounded-full animate-pulse" />
              </>
            ) : (
              <span className="text-gray-500">Offline</span>
            )}
          </div>
          
          {/* Quick Contact Info */}
          <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
            {intelligence.profile.email && (
              <div className="flex items-center gap-1">
                <Mail className="w-3 h-3" />
                <span className="truncate">{intelligence.profile.email}</span>
              </div>
            )}
            {intelligence.profile.location && (
              <div className="flex items-center gap-1">
                <MapPin className="w-3 h-3" />
                <span className="truncate">{intelligence.profile.location}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Device and Engagement Info */}
      <div className="grid grid-cols-2 gap-3">
        <div className={`p-3 rounded-lg border ${deviceConfig.bgColor} ${deviceConfig.borderColor}`}>
          <div className="flex items-center gap-2 mb-1">
            <deviceConfig.icon className={`w-4 h-4 ${deviceConfig.color}`} />
            <span className="text-xs font-medium text-gray-700">{deviceConfig.label}</span>
          </div>
          <p className="text-xs text-gray-600">{intelligence.device.browser}</p>
        </div>
        
        <div className={`p-3 rounded-lg border ${engagementLevel.bgColor} ${engagementLevel.borderColor}`}>
          <div className="flex items-center gap-2 mb-1">
            <engagementLevel.icon className={`w-4 h-4 ${engagementLevel.color}`} />
            <span className="text-xs font-medium text-gray-700">Engagement</span>
          </div>
          <p className="text-xs text-gray-600">{intelligence.engagement.engagementScore}/100</p>
        </div>
      </div>
      
    </div>
  )
}

/**
 * Customer Metrics Component
 * 
 * Displays engagement analytics and visit history
 */
const CustomerMetrics: React.FC<{
  intelligence: CustomerIntelligence
}> = ({ intelligence }) => {
  
  const metrics = [
    {
      label: 'Session Time',
      value: formatDuration(intelligence.engagement.sessionDuration),
      icon: Clock,
      color: 'text-blue-600'
    },
    {
      label: 'Messages',
      value: intelligence.engagement.messageCount.toString(),
      icon: MessageSquare,
      color: 'text-purple-600'
    },
    {
      label: 'Previous Visits',
      value: intelligence.engagement.previousVisits.toString(),
      icon: Calendar,
      color: 'text-emerald-600'
    },
    {
      label: 'Customer Type',
      value: intelligence.engagement.isReturning ? 'Returning' : 'New',
      icon: intelligence.engagement.isReturning ? Heart : UserPlus,
      color: intelligence.engagement.isReturning ? 'text-pink-600' : 'text-amber-600'
    }
  ]
  
  return (
    <div className="grid grid-cols-2 gap-3">
      {metrics.map((metric, index) => (
        <div key={index} className="p-3 bg-white border border-slate-200 rounded-lg">
          <div className="flex items-center gap-2 mb-1">
            <metric.icon className={`w-4 h-4 ${metric.color}`} />
            <span className="text-xs font-medium text-gray-700">{metric.label}</span>
          </div>
          <p className="text-sm font-semibold text-gray-900">{metric.value}</p>
        </div>
      ))}
    </div>
  )
}

/**
 * Technical Information Component
 * 
 * Displays detailed device and browser information
 */
const TechnicalInfo: React.FC<{
  intelligence: CustomerIntelligence
}> = ({ intelligence }) => {
  
  const technicalData = [
    {
      label: 'Operating System',
      value: intelligence.device.os,
      icon: Monitor
    },
    {
      label: 'Browser',
      value: intelligence.device.browser,
      icon: Globe
    },
    {
      label: 'Timezone',
      value: intelligence.profile.timezone || 'Unknown',
      icon: Clock
    },
    {
      label: 'Entry Point',
      value: intelligence.context.entryPoint,
      icon: ArrowRight
    }
  ]
  
  return (
    <div className="space-y-3">
      {technicalData.map((item, index) => (
        <div key={index} className="flex items-center justify-between py-2 border-b border-slate-100 last:border-0">
          <div className="flex items-center gap-2">
            <item.icon className="w-4 h-4 text-gray-400" />
            <span className="text-sm text-gray-600">{item.label}</span>
          </div>
          <span className="text-sm font-medium text-gray-900 truncate max-w-32">{item.value}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * Context Information Component
 * 
 * Displays page context and referrer information
 */
const ContextInfo: React.FC<{
  intelligence: CustomerIntelligence
}> = ({ intelligence }) => {
  
  if (!intelligence.context.currentPage && !intelligence.context.referrer) {
    return (
      <div className="text-center py-4 text-gray-500 text-sm">
        No context information available
      </div>
    )
  }
  
  return (
    <div className="space-y-3">
      {intelligence.context.currentPage && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Eye className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Current Page</span>
          </div>
          <a 
            href={intelligence.context.currentPage} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 break-all flex items-center gap-1"
          >
            {intelligence.context.currentPage}
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        </div>
      )}
      
      {intelligence.context.referrer && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ArrowRight className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Referrer</span>
          </div>
          <a 
            href={intelligence.context.referrer} 
            target="_blank" 
            rel="noopener noreferrer"
            className="text-sm text-blue-600 hover:text-blue-800 break-all flex items-center gap-1"
          >
            {intelligence.context.referrer}
            <ExternalLink className="w-3 h-3 flex-shrink-0" />
          </a>
        </div>
      )}
      
      {intelligence.context.utmSource && (
        <div>
          <div className="flex items-center gap-2 mb-1">
            <Tag className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Marketing Source</span>
          </div>
          <Badge variant="outline" className="text-xs">
            {intelligence.context.utmSource}
          </Badge>
        </div>
      )}
    </div>
  )
}

/**
 * Quick Actions Component
 * 
 * Action buttons for common agent tasks
 */
const QuickActions: React.FC<{
  conversation: EnhancedConversationAssignment
  onAction: (actionId: string) => void
}> = ({ conversation, onAction }) => {
  
  const handleAction = (actionId: string) => {
    logger.info('⚡ Quick action triggered', {
      component: 'CustomerIntelligencePanel',
      action: actionId,
      conversationId: conversation.conversation_id
    })
    
    onAction(actionId)
  }
  
  return (
    <div className="space-y-2">
      {QUICK_ACTIONS.map((action) => (
        <Button
          key={action.id}
          onClick={() => handleAction(action.id)}
          className={`w-full justify-start gap-2 text-white ${action.color}`}
          size="sm"
        >
          <action.icon className="w-4 h-4" />
          {action.label}
        </Button>
      ))}
    </div>
  )
}

/**
 * Customer Intelligence Panel
 * 
 * Main component that orchestrates all customer intelligence features
 */
export const CustomerIntelligencePanel: React.FC<CustomerIntelligencePanelProps> = ({
  conversation,
  customerIntelligence,
  currentUser,
  viewMode = 'standard',
  className = '',
  onAssignmentComplete,
  onPriorityUpdate
}) => {
  
  // Component initialization
  useEffect(() => {
    logger.info('🧠 CustomerIntelligencePanel initialized', {
      component: 'CustomerIntelligencePanel',
      hasConversation: !!conversation,
      hasIntelligence: !!customerIntelligence,
      viewMode
    })
  }, [])

  // Debug customer intelligence updates
  useEffect(() => {
    logger.info('🔍 CustomerIntelligencePanel customerIntelligence updated', {
      component: 'CustomerIntelligencePanel',
      hasCustomerIntelligence: !!customerIntelligence,
      customerName: customerIntelligence?.profile?.name,
      conversationId: conversation?.conversation_id,
      timestamp: new Date().toISOString(),
      // Add debug info to see if we're getting undefined/null between updates
      intelligenceState: customerIntelligence ? 'has-data' : 'null-or-undefined'
    })
  }, [customerIntelligence, conversation?.conversation_id])

  // Debug conversation updates
  useEffect(() => {
    logger.info('🔍 CustomerIntelligencePanel conversation updated', {
      component: 'CustomerIntelligencePanel',
      hasConversation: !!conversation,
      conversationId: conversation?.conversation_id,
      customerName: conversation?.visitor_name,
      timestamp: new Date().toISOString()
    })
  }, [conversation])

  // State for expandable sections
  const [expandedSections, setExpandedSections] = useState({
    profile: true,
    metrics: true,
    technical: false,
    context: false,
    actions: true
  })

  // State for escalation modal
  const [showEscalationModal, setShowEscalationModal] = useState(false)
  const [availableAgents, setAvailableAgents] = useState<any[]>([])
  const [escalationLoading, setEscalationLoading] = useState(false)
  const [selectedAgent, setSelectedAgent] = useState<any>(null)
  
  // Local state for conversation priority to ensure UI updates
  const [localPriority, setLocalPriority] = useState<string>(conversation?.priority || 'normal')

  // Loading states for quick actions
  const [actionLoading, setActionLoading] = useState<string | null>(null)
  
  // Listen for priority updates from other components
  useEffect(() => {
    if (!conversation) return
    
    // Initialize local priority from conversation
    setLocalPriority(conversation.priority || 'normal')
    
    // Event handler for priority updates
    const handlePriorityUpdated = (event: CustomEvent) => {
      const { conversationId, newPriority } = event.detail || {}
      
      // Only update if this is for our conversation
      if (conversationId === conversation.conversation_id) {
        logger.info('📣 CustomerIntelligencePanel received priorityUpdated event', {
          conversationId,
          newPriority,
          currentPriority: localPriority
        })
        
        // Update local state
        setLocalPriority(newPriority)
      }
    }
    
    // Add event listener
    window.addEventListener('priorityUpdated', handlePriorityUpdated as EventListener)
    
    // Cleanup
    return () => {
      window.removeEventListener('priorityUpdated', handlePriorityUpdated as EventListener)
    }
  }, [conversation, localPriority])

  // Toast notifications
  const { toast } = useToast()
  
  // Get current user from auth hook if not passed as prop
  const { user: authUser } = useAuth()
  const effectiveUser = currentUser || authUser
  
  // Check if current user can transfer conversation
  const canTransferToAI = useMemo(() => {
    if (!conversation || !effectiveUser) {
      logger.info('🔍 Transfer permission: Missing conversation or user', {
        hasConversation: !!conversation,
        hasUser: !!effectiveUser
      })
      return false
    }
    
    // Company owners and admins can always transfer any conversation
    if (effectiveUser.user_type === 'company_owner' || effectiveUser.user_type === 'admin') {
      logger.info('🔍 Transfer permission: Company owner/admin can transfer any conversation', {
        userType: effectiveUser.user_type,
        userEmail: effectiveUser.email,
        conversationStatus: conversation.status,
        assignedAgent: conversation.assigned_agent_name
      })
      return true
    }
    
    // Agents can only transfer if they are assigned to the conversation
    if (effectiveUser.user_type === 'agent') {
      // Check by assigned agent email or name
      const isAssignedByEmail = conversation.assigned_agent_email === effectiveUser.email
      const isAssignedByName = conversation.assigned_agent_name === effectiveUser.full_name
      
      logger.info('🔍 Transfer permission check for agent', {
        userEmail: effectiveUser.email,
        userFullName: effectiveUser.full_name,
        assignedAgentEmail: conversation.assigned_agent_email,
        assignedAgentName: conversation.assigned_agent_name,
        isAssignedByEmail,
        isAssignedByName,
        canTransfer: isAssignedByEmail || isAssignedByName
      })
      
      return isAssignedByEmail || isAssignedByName
    }
    
    logger.info('🔍 Transfer permission: User type not allowed', {
      userType: effectiveUser.user_type
    })
    return false
  }, [conversation, effectiveUser])

  /**
   * Handle quick actions with actual API integration
   */
  const handleQuickAction = useCallback(async (actionId: string) => {
    if (!conversation) return
    
    setActionLoading(actionId)
    
    logger.info('🚀 Customer intelligence action initiated', {
      component: 'CustomerIntelligencePanel',
      action: actionId,
      conversationId: conversation.conversation_id,
      customerName: conversation.visitor_name
    })
    
    try {
      switch (actionId) {
        case 'assign':
          // Assign conversation to current user (agents, company owners, or admins)
          // Use passed currentUser or fallback to localStorage with enhanced user detection
          let user = currentUser
          if (!user) {
            try {
              const authStorage = localStorage.getItem('bgai-auth-storage')
              if (authStorage) {
                const parsed = JSON.parse(authStorage)
                user = parsed?.state?.user || parsed?.user
              }
            } catch (parseError) {
              logger.error('Failed to parse auth storage', parseError)
            }
          }
          
          // Enhanced user type detection - check multiple possible fields
          const userType = user?.user_type || user?.role || 'unknown'
          
          // If still no user type detected, check localStorage directly for user_type
          let finalUserType = userType
          if (userType === 'unknown' || !userType) {
            try {
              const storedUserType = localStorage.getItem('user_type')
              if (storedUserType) {
                finalUserType = storedUserType
                logger.info('🔧 Retrieved user type from localStorage fallback', {
                  storedUserType,
                  originalUserType: userType
                })
              }
            } catch (error) {
              logger.warn('Failed to get user type from localStorage', error)
            }
          }
          
          logger.info('🔍 User permission check for assignment', {
            component: 'CustomerIntelligencePanel',
            hasUser: !!user,
            userEmail: user?.email,
            detectedUserType: finalUserType,
            wasPassedAsProps: !!currentUser,
            userObjectHasUserType: !!(user?.user_type),
            userObjectHasRole: !!(user?.role)
          })
          
          const canAssign = finalUserType === 'agent' || finalUserType === 'company_owner' || finalUserType === 'admin'
          
          if (user && canAssign) {
            // Variables to store agent data for use in callback later
            let agentName: string = user.name || user.email
            let agentId: string = user.id // Default fallback
            
            // Get user's agent profile to use agent ID instead of user ID
            try {
              logger.info('🔍 Getting user agent profile for assignment')
              const response = await api.get(`/live-chat/conversations/${conversation.conversation_id}/available-agents?include_self=true`)
              const availableAgents = response.data.agents || []
              
              const userAgent = availableAgents.find(agent => 
                agent.email === user.email || 
                agent.user_account_id === user.id
              )
              
              if (!userAgent) {
                logger.warn('⚠️ No agent profile found for user', {
                  userEmail: user.email,
                  userId: user.id,
                  userType: finalUserType
                })
                toast({
                  title: 'Assignment Failed',
                  description: 'No agent profile found. Please contact admin to set up your agent profile.',
                  variant: 'destructive'
                })
                return
              }
              
              logger.info('✅ Found user agent profile', {
                agentId: userAgent.id,
                agentName: userAgent.name,
                agentRole: userAgent.role,
                // 🔍 DEBUG: Log all available fields to see what we have
                allFields: Object.keys(userAgent),
                fullUserAgent: userAgent
              })
              
              // Update agent data with fallbacks before using in API call
              agentName = userAgent.name || userAgent.display_name || userAgent.full_name || user.name || user.email
              agentId = userAgent.id
              
              const assignmentReason = finalUserType === 'company_owner' 
                ? 'Quick assignment by company owner'
                : finalUserType === 'admin'
                ? 'Quick assignment by admin'
                : 'Quick assignment from customer intelligence panel'
                
              const contextNote = finalUserType === 'company_owner'
                ? 'Company owner took ownership via quick action'
                : finalUserType === 'admin'
                ? 'Admin took ownership via quick action'
                : 'Agent took ownership via quick action'
              
              await conversationHistoryService.assignConversation(
                conversation.conversation_id,
                agentId, // Use captured agent ID
                assignmentReason,
                contextNote
              )
            
            logger.info('✅ Conversation assigned successfully via quick action', {
              assignedBy: finalUserType,
              userId: user.id,
              conversationId: conversation.conversation_id,
              assignedToAgent: agentName
            })

            toast({
              title: 'Conversation Assigned',
              description: `Successfully assigned to ${agentName}`,
              variant: 'success'
            })
            
            } catch (agentError) {
              logger.error('❌ Failed to get agent profile', agentError)
              toast({
                title: 'Assignment Failed',
                description: 'Failed to get agent profile. Please try again.',
                variant: 'destructive'
              })
              return
            }
            
            // Real-time WebSocket notifications will handle the UI updates automatically
            logger.info('🔄 Assignment completed, WebSocket notifications will update the UI')
            
            // Notify parent component - this may trigger a manual refresh if needed
            if (onAssignmentComplete) {
              logger.info('🔄 Triggering assignment complete callback')
              // Use captured agent data (already available in scope)
              const assignedAgentName = agentName
              const assignedAgentId = agentId
              
              // Add a small delay to allow WebSocket updates to arrive first
              setTimeout(() => {
                onAssignmentComplete({ 
                  status: 'assigned', 
                  conversationId: conversation.conversation_id,
                  agentName: assignedAgentName,
                  agentId: assignedAgentId
                })
              }, 500)
            }
          } else {
            logger.warn('⚠️ Current user cannot assign conversations', {
              userType: finalUserType,
              hasUser: !!user,
              wasPassedAsProps: !!currentUser
            })
            toast({
              title: 'Permission Denied',
              description: 'Only agents, company owners, and admins can assign conversations to themselves',
              variant: 'destructive'
            })
          }
          break
          
        case 'transfer':
          // Transfer back to AI using live chat service
          logger.info('🔄 Attempting to transfer conversation to AI', {
            conversationId: conversation.conversation_id,
            assignedAgentName: conversation.assigned_agent_name,
            assignedAgentId: conversation.assigned_agent_id,
            currentUserName: effectiveUser?.name,
            currentUserEmail: effectiveUser?.email,
            currentUserId: effectiveUser?.id
          })
          
          try {
            await liveChatService.transferToAi(conversation.conversation_id)
            logger.info('✅ Conversation transferred back to AI successfully')
            
            toast({
              title: 'Conversation Transferred',
              description: 'Successfully transferred conversation back to AI assistant',
              variant: 'success'
            })
          } catch (transferError: any) {
            logger.error('❌ Transfer failed', transferError)
            
            // Debug log to see error structure
            console.log('🔍 Transfer error structure:', {
              hasResponse: !!transferError.response,
              responseData: transferError.response?.data,
              responseDetail: transferError.response?.data?.detail,
              errorMessage: transferError.message,
              fullError: transferError
            })
            
            // Show more specific error message
            const errorMessage = transferError.response?.data?.detail || 
                                transferError.message || 
                                'Unable to transfer conversation. Please try again.'
            
            toast({
              title: 'Transfer Failed',
              description: errorMessage,
              variant: 'destructive'
            })
            
            // Debug: Also log that toast was called
            console.log('🔍 Toast called with:', {
              title: 'Transfer Failed',
              description: errorMessage,
              variant: 'destructive'
            })
            // Don't re-throw, error is handled
            return
          }
          
          // WebSocket will handle UI updates automatically
          if (onAssignmentComplete) {
            logger.info('🔄 Triggering assignment complete callback after transfer')
            onAssignmentComplete({ status: 'ai_active', conversationId: conversation.conversation_id })
          }
          break
          
        case 'resolve':
          // Mark conversation as resolved using live chat service
          try {
            await liveChatService.resolveConversation(
              conversation.conversation_id,
              'Resolved via customer intelligence panel quick action'
            )
            logger.info('✅ Conversation resolved successfully')
            
            toast({
              title: 'Conversation Resolved',
              description: 'Conversation has been successfully marked as resolved.',
              variant: 'success'
            })
          } catch (resolveError: any) {
            logger.error('❌ Resolve failed', resolveError)
            
            const errorMessage = resolveError.response?.data?.detail || 
                                resolveError.message || 
                                'Unable to resolve conversation. Please try again.'
            
            toast({
              title: 'Resolve Failed',
              description: errorMessage,
              variant: 'destructive'
            })
            return
          }
          
          // WebSocket will handle UI updates automatically
          if (onAssignmentComplete) {
            logger.info('🔄 Triggering assignment complete callback after resolve')
            onAssignmentComplete({ status: 'resolved', conversationId: conversation.conversation_id })
          }
          break
          
        case 'escalate':
          // Show assignment popup to choose agent/supervisor
          logger.info('🚨 Opening assignment popup')
          setSelectedAgent(null) // Reset selection
          setShowEscalationModal(true)
          break
          
          
        default:
          logger.warn('❓ Unknown quick action:', actionId)
      }
    } catch (error) {
      logger.error('❌ Quick action failed', {
        component: 'CustomerIntelligencePanel',
        action: actionId,
        error: error instanceof Error ? error.message : 'Unknown error'
      })
      
      const actionLabels = {
        assign: 'assign',
        transfer: 'transfer',
        resolve: 'resolve',
        escalate: 'escalate'
      }
      
      toast({
        title: 'Action Failed',
        description: `Failed to ${actionLabels[actionId] || actionId} conversation: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      })
    } finally {
      setActionLoading(null)
    }
  }, [conversation, currentUser, toast, onAssignmentComplete])

  /**
   * Load available agents for escalation
   */
  const loadAvailableAgents = useCallback(async () => {
    try {
      setEscalationLoading(true)
      logger.info('🔍 Loading available agents for escalation')
      
      const agents = await conversationHistoryService.getAvailableAgents()
      
      logger.info('🔍 Debug: All agents loaded', {
        agentCount: agents.length,
        agents: agents.map(a => ({
          id: a.id,
          name: a.name,
          email: a.email,
          role: a.role,
          skills: a.skills,
          is_active: a.is_active
        }))
      })
      
      // Filter agents who can handle escalations (excluding current user)
      const currentUserEmail = currentUser?.email
      logger.info('🔍 Debug: Current user email', currentUserEmail)
      
      // Make filtering more inclusive - show all agents except current user
      const escalationAgents = agents.filter(agent => {
        const isCurrentUser = agent.email === currentUserEmail
        const isActive = agent.is_active
        const canEscalate = (
          agent.role === 'admin' || 
          agent.role === 'supervisor' || 
          agent.role === 'lead' ||
          agent.role === 'agent' || // Include regular agents too
          (agent.skills && agent.skills.includes('escalation'))
        )
        
        logger.info(`🔍 Debug: Agent ${agent.name}`, {
          isCurrentUser,
          isActive,
          canEscalate,
          role: agent.role,
          skills: agent.skills,
          willInclude: !isCurrentUser && isActive && canEscalate
        })
        
        return !isCurrentUser && isActive && canEscalate
      })
      
      // Sort agents by availability: online + capacity available first, then offline
      escalationAgents.sort((a, b) => {
        // First priority: online status
        const aOnline = a.is_online || false
        const bOnline = b.is_online || false
        if (aOnline !== bOnline) return bOnline ? 1 : -1
        
        // Second priority: available capacity
        const aAvailable = a.current_chat_count < a.max_concurrent_chats
        const bAvailable = b.current_chat_count < b.max_concurrent_chats
        if (aAvailable !== bAvailable) return bAvailable ? 1 : -1
        
        // Third priority: role hierarchy (admin > supervisor > lead > agent)
        const roleOrder = { admin: 4, supervisor: 3, lead: 2, agent: 1 }
        const aRoleScore = roleOrder[a.role] || 1
        const bRoleScore = roleOrder[b.role] || 1
        return bRoleScore - aRoleScore
      })
      
      setAvailableAgents(escalationAgents)
      logger.info(`✅ Found ${escalationAgents.length} available escalation agents`)
      
    } catch (error) {
      logger.error('❌ Failed to load available agents', error)
      toast({
        title: 'Loading Failed',
        description: 'Failed to load available agents. Please try again.',
        variant: 'destructive'
      })
    } finally {
      setEscalationLoading(false)
    }
  }, [currentUser])

  /**
   * Handle escalation to selected agent
   */
  const handleEscalation = useCallback(async (selectedAgent: any) => {
    if (!conversation || !selectedAgent) return
    
    try {
      logger.info('🚨 Escalating conversation to selected agent', {
        conversationId: conversation.conversation_id,
        selectedAgentId: selectedAgent.id,
        selectedAgentName: selectedAgent.name
      })
      
      await conversationHistoryService.assignConversation(
        conversation.conversation_id,
        selectedAgent.id,
        `Escalated to ${selectedAgent.role || 'agent'}: ${selectedAgent.name}`,
        `Escalated from customer intelligence panel due to complexity or special requirements`
      )
      
      logger.info('✅ Conversation escalated successfully')
      
      toast({
        title: 'Conversation Escalated',
        description: `Successfully escalated to ${selectedAgent.name}`,
        variant: 'success'
      })
      
      setShowEscalationModal(false)
      setSelectedAgent(null)
      
      // Your real-time notification system will handle the UI updates automatically
      logger.info('🔄 Escalation complete - real-time notifications will update UI', {
        conversationId: conversation.conversation_id,
        escalatedTo: selectedAgent.name,
        escalatedToId: selectedAgent.id
      })
      
      // Notify parent component
      if (onAssignmentComplete) {
        logger.info('🔄 Triggering assignment complete callback after escalation')
        // Add a small delay to allow WebSocket updates to arrive first
        setTimeout(() => {
          onAssignmentComplete({ 
            status: 'escalated', 
            conversationId: conversation.conversation_id,
            agentName: selectedAgent.name,
            agentId: selectedAgent.id
          })
        }, 500)
      }
      
    } catch (error) {
      logger.error('❌ Escalation failed', error)
      toast({
        title: 'Escalation Failed',
        description: `Failed to escalate conversation: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: 'destructive'
      })
    }
  }, [conversation, onAssignmentComplete])

  /**
   * Open escalation modal and load agents
   */
  useEffect(() => {
    if (showEscalationModal && availableAgents.length === 0) {
      loadAvailableAgents()
    }
  }, [showEscalationModal, availableAgents.length, loadAvailableAgents])

  /**
   * Toggle section expansion
   */
  const toggleSection = useCallback((section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }))
  }, [])

  // Create fallback intelligence data from conversation if not provided
  // Use useMemo to ensure component re-renders when data changes
  const intelligence = useMemo(() => {
    if (!conversation) {
      logger.info('🔍 CustomerIntelligencePanel: No conversation provided', {
        component: 'CustomerIntelligencePanel',
        hasConversation: !!conversation
      })
      return null
    }
    
    // If we have customerIntelligence prop, use it directly
    if (customerIntelligence) {
      logger.info('🔍 CustomerIntelligencePanel: Using provided customerIntelligence', {
        component: 'CustomerIntelligencePanel',
        hasCustomerIntelligence: true,
        customerName: customerIntelligence.profile.name,
        browser: customerIntelligence.device.browser,
        isOnline: customerIntelligence.profile.isOnline,
        conversationId: conversation.conversation_id,
        timestamp: new Date().toISOString()
      })
      return customerIntelligence
    }
    
    // Otherwise create fallback from conversation data
    const fallbackIntelligence = {
      profile: {
        name: conversation.visitor_name || 'Anonymous Visitor',
        email: conversation.visitor_email,
        phone: conversation.visitor_phone,
        location: conversation.city && conversation.country 
          ? `${conversation.city}, ${conversation.country}` 
          : 'Unknown location',
        timezone: conversation.timezone,
        isOnline: conversation.is_active || false  // Uses session-based is_active from backend
      },
      device: {
        type: (conversation.device_type?.toLowerCase() as any) || 'unknown',
        browser: conversation.browser_name && conversation.browser_version
          ? `${conversation.browser_name} ${conversation.browser_version}`
          : 'Unknown browser',
        os: conversation.operating_system || 'Unknown OS'
      },
      engagement: {
        sessionDuration: conversation.session_duration || 0,
        messageCount: conversation.total_messages || 0,
        satisfaction: conversation.satisfaction_rating,
        previousVisits: conversation.previous_visits || 0,
        isReturning: conversation.is_returning_visitor || false,
        engagementScore: 50 // Default score
      },
      context: {
        currentPage: conversation.page_url,
        referrer: conversation.referrer_url,
        utmSource: conversation.utm_source,
        entryPoint: conversation.referrer_url ? 'External' : 'Direct'
      }
    }
    
    logger.info('🔍 CustomerIntelligencePanel: Using fallback intelligence', {
      component: 'CustomerIntelligencePanel',
      hasCustomerIntelligence: false,
      fallbackCustomerName: fallbackIntelligence.profile.name,
      fallbackBrowser: fallbackIntelligence.device.browser,
      conversationId: conversation.conversation_id,
      timestamp: new Date().toISOString()
    })
    
    return fallbackIntelligence
  }, [customerIntelligence, conversation])

  // Show empty state when no conversation is selected
  if (!conversation || !intelligence) {
    return (
      <div className={`flex flex-col h-full bg-white ${className}`}>
        <div className="flex-1 flex items-center justify-center p-6">
          <div className="text-center max-w-sm">
            <User className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Customer Intelligence
            </h3>
            <p className="text-gray-600 text-sm">
              Select a conversation to view detailed customer insights, engagement analytics, 
              and quick action tools.
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col h-full bg-slate-50 ${className}`}>
      
      {/* Panel Header */}
      <div className="p-4 bg-white border-b border-slate-200">
        <div className="flex items-center justify-between mb-2">
          <h2 className="font-semibold text-gray-900">Customer Intelligence</h2>
          <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
            <MoreVertical className="w-4 h-4" />
          </Button>
        </div>
        <p className="text-xs text-gray-600 mb-3">
          {intelligence ? `${intelligence.profile.name} • ${intelligence.device.browser}` : 'Real-time insights and engagement analytics'}
        </p>
        
        {/* Debug indicator */}
        {process.env.NODE_ENV === 'development' && (
          <div className="text-xs bg-yellow-100 text-yellow-800 p-1 rounded mb-2">
            Debug: {customerIntelligence ? 'Has customerIntelligence' : 'Using fallback'} | Intelligence: {intelligence ? 'Available' : 'None'}
          </div>
        )}
        
        {/* Conversation Summary - NEW SECTION */}
        <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200 mb-3">
          <div className="text-xs font-medium text-gray-700 mb-2">Conversation Summary</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="flex items-center gap-2 text-gray-600">
              <Clock className="w-4 h-4 text-blue-600" />
              <div>
                <div className="font-medium text-xs">Wait Time</div>
                <div className="text-sm">{conversation.wait_time ? `${conversation.wait_time}s` : '0s'}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <MessageSquare className="w-4 h-4 text-purple-600" />
              <div>
                <div className="font-medium text-xs">Messages</div>
                <div className="text-sm">{customerIntelligence?.engagement?.messageCount || conversation.total_messages || 0}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <Calendar className="w-4 h-4 text-emerald-600" />
              <div>
                <div className="font-medium text-xs">Started</div>
                <div className="text-sm">{new Date(conversation.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
              </div>
            </div>
            
            <div className="flex items-center gap-2 text-gray-600">
              <Flag className="w-4 h-4 text-amber-600" />
              <div className="flex-1">
                <div className="font-medium text-xs mb-1">Priority</div>
                <PrioritySelector
                  currentPriority={(localPriority || 'normal') as any}
                  conversationId={conversation.conversation_id}
                  size="xs"
                  variant="dropdown"
                  onPriorityChange={(newPriority, oldPriority) => {
                    logger.info('Priority changed in Customer Intelligence Panel', {
                      conversationId: conversation.conversation_id,
                      oldPriority,
                      newPriority
                    })
                    
                    // Update local state immediately for optimistic UI responsiveness
                    setLocalPriority(newPriority)
                    
                    // Call parent callback to update the conversation state
                    // WebSocket notifications will handle real-time updates across all connected users
                    onPriorityUpdate?.(conversation.conversation_id, newPriority, oldPriority)
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions at top of Customer Intelligence Panel */}
        <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
          <div className="text-xs font-medium text-gray-700 mb-2">Quick Actions</div>
          <div className="grid grid-cols-2 gap-2">
            <Button
              size="sm"
              className="h-8 px-2 text-xs bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => handleQuickAction('assign')}
              disabled={actionLoading === 'assign'}
            >
              {actionLoading === 'assign' ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <UserCheck className="w-3 h-3 mr-1" />
              )}
              Assign to Me
            </Button>
            <Button
              size="sm" 
              className={`h-8 px-2 text-xs ${
                !canTransferToAI 
                  ? 'bg-purple-400 cursor-not-allowed opacity-60' 
                  : 'bg-purple-600 hover:bg-purple-700'
              } text-white`}
              onClick={() => handleQuickAction('transfer')}
              disabled={actionLoading === 'transfer' || !canTransferToAI}
              title={
                !canTransferToAI 
                  ? conversation?.assigned_agent_name 
                    ? `Only ${conversation.assigned_agent_name} can transfer this conversation`
                    : 'Only assigned agent or company owner can transfer conversation'
                  : 'Transfer conversation back to AI'
              }
            >
              {actionLoading === 'transfer' ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <RotateCcw className="w-3 h-3 mr-1" />
              )}
              Transfer to AI
            </Button>
            <Button
              size="sm"
              className="h-8 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={() => handleQuickAction('resolve')}
              disabled={actionLoading === 'resolve'}
            >
              {actionLoading === 'resolve' ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <CheckCircle2 className="w-3 h-3 mr-1" />
              )}
              Mark Resolved
            </Button>
            <Button
              size="sm"
              className="h-8 px-2 text-xs bg-amber-600 hover:bg-amber-700 text-white"
              onClick={() => handleQuickAction('escalate')}
              disabled={actionLoading === 'escalate'}
            >
              {actionLoading === 'escalate' ? (
                <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
              ) : (
                <AlertTriangle className="w-3 h-3 mr-1" />
              )}
              Assign to...
            </Button>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        
        {/* Customer Profile Section */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Customer Profile
              {process.env.NODE_ENV === 'development' && (
                <span className="ml-2 text-xs bg-green-100 text-green-800 px-1 rounded">
                  {intelligence?.profile?.name || 'No name'}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intelligence ? (
              <CustomerProfileHeader 
                intelligence={intelligence} 
                conversation={conversation} 
              />
            ) : (
              <div className="text-sm text-gray-500">No customer data available</div>
            )}
          </CardContent>
        </Card>
        
        {/* Technical Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Technical Details
              {process.env.NODE_ENV === 'development' && (
                <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-1 rounded">
                  {intelligence?.device?.browser || 'No browser'}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intelligence ? (
              <TechnicalInfo intelligence={intelligence} />
            ) : (
              <div className="text-sm text-gray-500">No technical data available</div>
            )}
          </CardContent>
        </Card>
        
        {/* Context Information */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              Visit Context
              {process.env.NODE_ENV === 'development' && (
                <span className="ml-2 text-xs bg-purple-100 text-purple-800 px-1 rounded">
                  {intelligence?.context?.currentPage ? 'Has page' : 'No page'}
                </span>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {intelligence ? (
              <ContextInfo intelligence={intelligence} />
            ) : (
              <div className="text-sm text-gray-500">No context data available</div>
            )}
          </CardContent>
        </Card>
        
        
      </div>
      
      {/* Escalation Modal */}
      {showEscalationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4 max-h-[80vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-gray-900">Assign Conversation</h3>
              </div>
              <button
                onClick={() => {
                  setShowEscalationModal(false)
                  setSelectedAgent(null)
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>
            
            <p className="text-sm text-gray-600 mb-2">
              Select an agent, supervisor, or admin to assign this conversation to. 
              <strong>Skills, availability, and online status</strong> are shown for each agent:
            </p>
            
            {/* Status and Skills Legend */}
            <div className="bg-gray-50 rounded-lg p-3 mb-4 space-y-3">
              {/* Availability Status Legend */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Activity className="w-3 h-3 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">Availability Status:</span>
                </div>
                <div className="flex flex-wrap gap-2 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 rounded">Available</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 border border-yellow-200 rounded">Busy</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 border border-red-200 rounded">Offline</span>
                  </div>
                </div>
              </div>
              
              {/* Skills Legend */}
              <div>
                <div className="flex items-center gap-1 mb-2">
                  <Tag className="w-3 h-3 text-gray-500" />
                  <span className="text-xs font-medium text-gray-700">Skill Types:</span>
                </div>
                <div className="flex flex-wrap gap-1 text-xs">
                  <span className="px-2 py-0.5 bg-red-100 text-red-700 border border-red-200 rounded-full">assignment</span>
                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 border border-purple-200 rounded-full">admin</span>
                  <span className="px-2 py-0.5 bg-blue-100 text-blue-700 border border-blue-200 rounded-full">support</span>
                  <span className="px-2 py-0.5 bg-green-100 text-green-700 border border-green-200 rounded-full">technical</span>
                  <span className="px-2 py-0.5 bg-yellow-100 text-yellow-700 border border-yellow-200 rounded-full">billing</span>
                  <span className="px-2 py-0.5 bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-full">sales</span>
                </div>
              </div>
            </div>
            
            {escalationLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-2 text-gray-600">Loading agents...</span>
              </div>
            ) : availableAgents.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-amber-400" />
                <p className="text-gray-600 mb-4">No assignment agents available at the moment.</p>
                <Button
                  onClick={() => setShowEscalationModal(false)}
                  variant="outline"
                  size="sm"
                >
                  Close
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {availableAgents.map((agent) => {
                  const isOnline = agent.is_online || false
                  const hasCapacity = agent.current_chat_count < agent.max_concurrent_chats
                  const isAvailable = isOnline && hasCapacity
                  
                  return (
                    <div
                      key={agent.id}
                      className={`border rounded-lg p-3 cursor-pointer transition-colors relative ${
                        selectedAgent?.id === agent.id
                          ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
                          : isAvailable 
                          ? 'border-green-200 bg-green-50 hover:border-green-300 hover:bg-green-100' 
                          : isOnline && !hasCapacity
                          ? 'border-yellow-200 bg-yellow-50 hover:border-yellow-300 hover:bg-yellow-100'
                          : 'border-gray-200 bg-gray-50 hover:border-gray-300 hover:bg-gray-100 opacity-75'
                      }`}
                      onClick={() => setSelectedAgent(agent)}
                    >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2 mb-1">
                          <div className="flex items-center gap-1">
                            <div className={`w-2 h-2 rounded-full ${
                              isOnline ? 'bg-green-500' : 'bg-gray-400'
                            }`} />
                            <h4 className="font-medium text-gray-900">{agent.name}</h4>
                          </div>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              agent.role === 'admin' ? 'border-purple-200 text-purple-700 bg-purple-50' :
                              agent.role === 'supervisor' ? 'border-orange-200 text-orange-700 bg-orange-50' :
                              agent.role === 'lead' ? 'border-blue-200 text-blue-700 bg-blue-50' :
                              'border-gray-200 text-gray-700 bg-gray-50'
                            }`}
                          >
                            {agent.role || 'Agent'}
                          </Badge>
                          {/* Availability Status */}
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              isAvailable 
                                ? 'border-green-200 text-green-700 bg-green-50' 
                                : isOnline && !hasCapacity
                                ? 'border-yellow-200 text-yellow-700 bg-yellow-50'
                                : 'border-red-200 text-red-700 bg-red-50'
                            }`}
                          >
                            {isAvailable ? 'Available' : isOnline ? 'Busy' : 'Offline'}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{agent.email}</p>
                        
                        {/* Skills Section - More Prominent */}
                        {agent.skills && agent.skills.length > 0 ? (
                          <div className="mb-2">
                            <div className="flex items-center gap-1 mb-1">
                              <Tag className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-700">Skills:</span>
                            </div>
                            <div className="flex flex-wrap gap-1">
                              {agent.skills.map((skill: string) => (
                                <span
                                  key={skill}
                                  className={`text-xs px-2 py-1 rounded-full font-medium ${
                                    skill === 'assignment' || skill === 'escalation' ? 'bg-red-100 text-red-700 border border-red-200' :
                                    skill === 'admin' ? 'bg-purple-100 text-purple-700 border border-purple-200' :
                                    skill === 'support' ? 'bg-blue-100 text-blue-700 border border-blue-200' :
                                    skill === 'technical' ? 'bg-green-100 text-green-700 border border-green-200' :
                                    skill === 'billing' ? 'bg-yellow-100 text-yellow-700 border border-yellow-200' :
                                    skill === 'sales' ? 'bg-indigo-100 text-indigo-700 border border-indigo-200' :
                                    'bg-gray-100 text-gray-700 border border-gray-200'
                                  }`}
                                >
                                  {skill}
                                </span>
                              ))}
                            </div>
                          </div>
                        ) : (
                          <div className="mb-2">
                            <div className="flex items-center gap-1 mb-1">
                              <Tag className="w-3 h-3 text-gray-400" />
                              <span className="text-xs font-medium text-gray-500">No specific skills listed</span>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-gray-500">
                          {agent.current_chat_count}/{agent.max_concurrent_chats} chats
                        </div>
                        <div className="w-12 h-2 bg-gray-200 rounded-full mt-1">
                          <div
                            className={`h-2 rounded-full ${
                              (agent.current_chat_count / agent.max_concurrent_chats) < 0.7 
                                ? 'bg-green-500' 
                                : 'bg-yellow-500'
                            }`}
                            style={{
                              width: `${Math.min(100, (agent.current_chat_count / agent.max_concurrent_chats) * 100)}%`
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  )
                })}
              </div>
            )}
            
            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-gray-200">
              <Button
                onClick={() => {
                  setShowEscalationModal(false)
                  setSelectedAgent(null)
                }}
                variant="outline"
                size="sm"
              >
                Cancel
              </Button>
              <Button
                onClick={() => {
                  if (selectedAgent) {
                    handleEscalation(selectedAgent)
                  }
                }}
                disabled={!selectedAgent}
                size="sm"
                className="bg-blue-600 hover:bg-blue-700 text-white"
              >
                Assign to {selectedAgent?.name || 'Agent'}
              </Button>
            </div>
          </div>
        </div>
      )}
      
    </div>
  )
}

export default CustomerIntelligencePanel